import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import {
    CloudStereo,
    SaveSecretBody,
    WorkspaceBody,
    WorkspaceStereo,
    WorkspaceView,
} from '@lemoncloud/codes-goods-api';
import { WorkspaceRequestSet } from '@lemoncloud/codes-goods-api/dist/modules/codes/model';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Check, ChevronRight, Info, X } from 'lucide-react';
import moment from 'moment';

import {
    AWS,
    AdvDarkIcon,
    AdvIcon,
    Azure,
    BasicDarkIcon,
    BasicIcon,
    KeyInfoDarkImage,
    KeyInfoImage,
    ProDarkIcon,
    ProIcon,
} from '@codes/assets';
import { cloudsKeys, useClouds, useRegions } from '@codes/cloud';
import { useBillingDate } from '@codes/react-utils';
import { PrivacyTermsPage } from '@codes/shared';
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
    Input,
    Label,
    SharedSpinner,
    Sheet,
    SheetClose,
    SheetContent,
    SheetHeader,
    Skeleton,
    cn,
} from '@codes/ui-kit';
import { useWebCoreStore } from '@codes/web-core';
import { useCreateWorkspace, useEnrollAccessKey } from '@codes/workspace';

import { defaultRegiondCode } from './consts';
import { emailRegex, phoneRegex } from '../../consts';
import { useDialogStore } from '../../stores';
import { textRegex } from '../create-project-dialog';

interface CreateWorkspaceDialogProps {
    handleClose: () => void;
    handleComplete: (workspace: WorkspaceView) => void;
}

const DEFAULT_BODY: WorkspaceBody = {
    name: '',
    cloudId: '',
    regionId: '',
};

const DEFAULT_SECRET_BODY: SaveSecretBody = {
    accessKey: '',
    secretKey: '',
};

const DEFAULT_REQUEST_BODY: WorkspaceRequestSet = {
    email: '',
    phone: '',
};

type AccessKeyEnterMethod = 'manual' | 'auto' | '';

export function CreateWorkspaceDialog({ handleClose, handleComplete }: CreateWorkspaceDialogProps) {
    const [body, setBody] = useState<WorkspaceBody>({ ...DEFAULT_BODY });
    const [saveSecretBody, setSaveSecretBody] = useState<SaveSecretBody>({ ...DEFAULT_SECRET_BODY });

    const workspaceNameForm = useForm<WorkspaceBody>({
        defaultValues: {
            name: '',
            cloudId: '',
        },
    });

    const [watchedName] = useWatch({
        control: workspaceNameForm.control,
        name: ['name'],
    });

    const workspaceRequestForm = useForm<WorkspaceRequestSet>({
        defaultValues: {
            ...DEFAULT_REQUEST_BODY,
        },
    });

    const [watchedEmail, watchedPhone] = useWatch({
        control: workspaceRequestForm.control,
        name: ['email', 'phone'],
    });

    const { estimatedBillingDate } = useBillingDate();

    const onWorkspaceRequestSubmit = async (body: WorkspaceRequestSet) => {};

    const [accessKeyEnterMethod, setAccessKeyEnterMethod] = useState<AccessKeyEnterMethod>('');

    const isManual = accessKeyEnterMethod === 'manual';
    const isAuto = accessKeyEnterMethod === 'auto';

    const trimedBodyName = watchedName?.trim();

    const profile = useWebCoreStore(state => state.profile);

    const queryClient = useQueryClient();

    const { t } = useTranslation();

    const {
        data: clouds,
        isFetching: isFetchingClouds,
        isRefetching: isRefetchingClouds,
        error: cloudsFetchingError,
        refetch: refetchClouds,
    } = useClouds({});

    const {
        data: regions,
        isFetching: isFetchingRegions,
        isRefetching: isRefetchingRegions,
        error: regionsFetchingError,
        refetch: refetchRegions,
    } = useRegions({ limit: 100, cloudId: body?.cloudId });
    const {
        mutateAsync: createWorkspace,
        isSuccess,
        isPending: isCreatingWorkspace,
        data,
        error,
        reset: resetWorkspaceCreating,
    } = useCreateWorkspace();
    const {
        mutateAsync: enrollAccessKey,
        data: workspaceWithSecret,
        isPending: isEnrolling,
        reset: resetWorkspaceWithSecret,
        isError: isEnrollAccessKeyError,
        error: enrollAccessKeyError,
    } = useEnrollAccessKey();

    const {
        workspacePlan,
        setWorkspacePlan,
        setPriceDialog,
        isWorkspaceDialogOpen,
        setWorkspaceDialog,
        selectedWorkspacePlan,
        setSelectedWorkspacePlan,
    } = useDialogStore(state => state);

    const [agree, setAgree] = useState(false);

    const [selectedRegionId, setSelectedRegionId] = useState('');

    const checkedCloud = clouds?.list.find(cloud => cloud.id === body.cloudId);
    const isPro = checkedCloud && checkedCloud.planType === 'pro';

    const isRequestValid = useMemo(() => {
        return phoneRegex.test(watchedPhone as string) && emailRegex.test(watchedEmail as string);
    }, [phoneRegex, emailRegex, watchedEmail, watchedPhone]);

    const isAccessKeyEntered = useMemo(() => {
        if (!accessKeyEnterMethod) {
            return false;
        }

        if (isManual) {
            return !!workspaceWithSecret;
        }

        return isRequestValid;
    }, [accessKeyEnterMethod, watchedEmail, watchedPhone, workspaceWithSecret, isRequestValid]);

    const isDisabled = useMemo(() => {
        return !trimedBodyName || !body.cloudId || !agree || !textRegex.test(trimedBodyName);
    }, [isPro, trimedBodyName, body, agree]);

    const isEnrollAccessBtnDisabled = !saveSecretBody.accessKey || !saveSecretBody.secretKey || isEnrolling;

    const [isKeyFormOpen, setIsKeyFormOpen] = useState(false);
    const [isAutoKeyInfoFormOpen, setIsAutoKeyInfoFormOpen] = useState(false);

    const [isKeyInfoOpen, setIsKeyInfoOpen] = useState(false);
    const [isSelectRegionOpen, setIsSelectRegionOpen] = useState(false);

    const [proWorkspaceConfirmDialogOpened, setProWorkspaceConfirmDialogOpened] = useState(false);

    const defaultRegion = regions?.list.find(region => region.code === defaultRegiondCode);

    const selectedRegion = regions?.list.find(region => region.id === body.regionId);

    const scrollDivRef = useRef<HTMLDivElement>(null);

    const handleCloudCheck = (cloudId: string | undefined) => {
        if (!cloudId) {
            return;
        }

        const isCheckedCloud = cloudId === body.cloudId;

        if (isCheckedCloud) {
            setBody(prev => ({ ...prev, cloudId: '' }));
            return;
        }

        const cloud = clouds?.list.find(cloud => cloud.id === cloudId);

        if (!cloud) {
            return;
        }

        setWorkspacePlan(cloud.planType || null);
        setSelectedWorkspacePlan(null);
        setPriceDialog(true);

        // if (cloud.planType === 'pro') {
        //     setPriceDialog(true);
        //     setWorkspacePlan(null);
        //     return;
        // }

        // setBody(prev => ({
        //     ...prev,
        //     cloudId,
        // }));
    };

    const isCheckedCloud = (cloudId: string | undefined) => {
        if (!cloudId) {
            return false;
        }

        return body.cloudId === cloudId;
    };

    const getCloudImage = (stereo: CloudStereo | undefined) => {
        switch (stereo) {
            case 'aws':
                return AWS;
            case 'azure':
                return Azure;
            case 'gcp':
                return Google;
            default:
                return Google;
        }
    };

    const getCloudLabel = (workspaceStereo: WorkspaceStereo | undefined) => {
        switch (workspaceStereo) {
            case 'basic':
                return BasicIcon;
            case 'ent':
            case 'adv':
                return AdvIcon;
            case 'pro':
                return ProIcon;
            // TODO: have to change the new free icon
            case 'free':
                return BasicIcon;
        }
    };

    const getDarkCloudLabel = (workspaceStereo: WorkspaceStereo | undefined) => {
        switch (workspaceStereo) {
            case 'basic':
                return BasicDarkIcon;
            case 'ent':
            case 'adv':
                return AdvDarkIcon;
            case 'pro':
                return ProDarkIcon;
            case 'free':
                return BasicDarkIcon;
            default:
                return BasicDarkIcon;
        }
    };

    const onSubmit = () => {
        if (!body.cloudId) {
            toast({ title: t('workspace.create.cloudReqiredErrorText') });
            return;
        }

        if (!agree) {
            toast({ title: t('workspace.create.agreeRequiredErrorText') });
            return;
        }

        if (isPro && !!accessKeyEnterMethod) {
            if (isManual) {
                if (!workspaceWithSecret?.$secret?.id) {
                    toast({ title: t('workspace.create.accessKeyErrorText') });
                    return;
                }

                createWorkspace({
                    ...body,
                    name: watchedName?.trim(),
                    secretId: workspaceWithSecret?.$secret?.id,
                    stereo: 'pro',
                });
                return;
            }

            if (!isRequestValid) {
                toast({ title: t('workspace.create.autoCompletionErrorText') });
                return;
            }

            createWorkspace({
                ...body,
                name: watchedName?.trim(),
                request$: {
                    email: watchedEmail as string,
                    phone: watchedPhone as string,
                },
                stereo: 'pro',
            });
            return;
        }

        createWorkspace({ name: watchedName, cloudId: body.cloudId, stereo: checkedCloud?.planType });
    };

    const handleProWorkspaceCompleteBtnClick = () => {
        setProWorkspaceConfirmDialogOpened(false);
        handleComplete(data as WorkspaceView);
    };

    const handleCompleteToCreateDialog = (newWorkspace: WorkspaceView) => {
        if (isPro) {
            setProWorkspaceConfirmDialogOpened(true);
            return;
        }

        handleComplete && handleComplete(newWorkspace);
    };

    const resetData = async () => {
        setBody({ ...DEFAULT_BODY });
        setWorkspacePlan(null);
        setAgree(false);
        setSelectedWorkspacePlan(null);
        resetWorkspaceWithSecret();
        resetWorkspaceCreating();
        workspaceNameForm.reset();
        workspaceRequestForm.reset();
        resetSaveSecretBody();
        setAccessKeyEnterMethod('');

        await queryClient.resetQueries({ queryKey: cloudsKeys.list({}) });
    };

    const resetSaveSecretBody = () => {
        setSaveSecretBody({ ...DEFAULT_SECRET_BODY });
    };

    const openPriceDialog = () => {
        setPriceDialog(true);
        setSelectedWorkspacePlan(null);
    };

    const openSelectRegionDialog = () => {
        setSelectedRegionId(selectedRegion?.id || '');
        setIsSelectRegionOpen(true);
    };

    const closeSelectRegionDialog = () => {
        setIsSelectRegionOpen(false);
        setBody(prev => ({ ...prev, regionId: selectedRegionId }));
    };

    const accessKeyMethodBtnTapped = (method: AccessKeyEnterMethod) => {
        if (!!accessKeyEnterMethod && method === accessKeyEnterMethod) {
            setAccessKeyEnterMethod('');
            return;
        }
        setAccessKeyEnterMethod(method);
        workspaceRequestForm.reset();
    };

    const enterAccessKeyBtnTapped = () => {
        if (!accessKeyEnterMethod) {
            return;
        }

        if (isAuto) {
            setIsAutoKeyInfoFormOpen(true);
            return;
        }
        setIsKeyFormOpen(true);
    };

    const enrollAccessKeyBtnTapped = () => {
        if (isEnrollAccessBtnDisabled) {
            return;
        }

        if (workspaceWithSecret) {
            setIsKeyFormOpen(false);
            return;
        }

        enrollAccessKey(saveSecretBody);
    };

    const autoGenerateAccessKeyConfirmBtnTapped = () => {
        if (!isRequestValid) {
            return;
        }

        setIsAutoKeyInfoFormOpen(false);
    };

    const enrollAccessKeyErrorMessage = useMemo(() => {
        if (!enrollAccessKeyError) {
            return '';
        }
        const axiosError = enrollAccessKeyError as AxiosError<any>;

        const msg = axiosError.response?.data;

        if (!msg) {
            return t('accessKey.errorMessageText');
        }

        if (msg.includes('DUPLICATED')) {
            return t('accessKey.duplicatedErrorMessageText');
        }

        return t('accessKey.errorMessageText');
    }, [enrollAccessKeyError, isEnrollAccessKeyError]);

    const CloudList = useMemo(() => {
        if (isFetchingClouds || isRefetchingClouds) {
            return (
                <div className="flex gap-x-2 min-h-[90px]">
                    <Skeleton className="flex-1 " />
                    <Skeleton className="flex-1 " />
                    <Skeleton className="flex-1 " />
                </div>
            );
        }

        if (cloudsFetchingError) {
            return (
                <div className="min-h-[90px] flex items-center justify-center flex-col gap-y-2">
                    <div className="text-error">Failed to fetch clouds</div>
                    <Button onClick={() => refetchClouds()}>Retry</Button>
                </div>
            );
        }

        return (
            <ul className="grid grid-cols-3 gap-2 max-xs:grid-cols-1">
                {clouds?.list.map(cloud => (
                    <li
                        key={cloud.id}
                        className={cn(
                            'rounded-[16px] border h-[90px] relative overflow-hidden transition-colors',
                            isCheckedCloud(cloud.id) ? 'border-point' : 'border-[#F0F0F0] dark:border-[#5F5F5F]',
                            'hover:border-gradient'
                        )}
                    >
                        <label className="circle-check w-full cursor-pointer flex flex-col justify-center items-center gap-0">
                            <input
                                type="checkbox"
                                checked={isCheckedCloud(cloud.id)}
                                readOnly
                                onClick={() => handleCloudCheck(cloud.id)}
                                disabled={cloud.planType === 'adv' || cloud.planType === 'ent'}
                            />
                            <span className="self-start ml-3 mt-3"></span>
                            <div className="w-full flex flex-col justify-center items-center gap-[6px] -mt-3">
                                <Image className="h-8" src={getCloudImage(cloud.stereo)} alt="im image" />
                            </div>
                        </label>
                        <Image
                            className="absolute top-0 right-0"
                            src={getCloudLabel(cloud.planType)}
                            darkSrc={getDarkCloudLabel(cloud.planType)}
                        />
                    </li>
                ))}
            </ul>
        );
    }, [isFetchingClouds, isRefetchingClouds, isCheckedCloud, clouds, cloudsFetchingError]);

    const RegionButton = useMemo(() => {
        if (isRefetchingRegions || isFetchingRegions) {
            return <Skeleton className="h-[52px]" />;
        }

        if (regionsFetchingError) {
            return (
                <div className="flex flex-col gap-y-1">
                    <div className="text-error">Failed to fetch regions</div>
                    <Button onClick={() => refetchRegions()}>Retry</Button>
                </div>
            );
        }

        return (
            <DialogTrigger asChild>
                <button
                    type="button"
                    className="w-full h-[52px] px-[18px] rounded-[10px] bg-text-input-bg border-text-input-focus-border border flex items-center justify-between"
                    onClick={openSelectRegionDialog}
                >
                    <div className="text-sm flex items-center gap-[6px] font-medium">
                        <Check className="w-4 h-4" />
                        {selectedRegion?.name}
                    </div>
                    <ChevronRight className="w-5 h-5" />
                </button>
            </DialogTrigger>
        );
    }, [regions, isFetchingRegions, isRefetchingRegions, regionsFetchingError, selectedRegion, openSelectRegionDialog]);

    useEffect(() => {
        if (!isSuccess || !data) {
            return;
        }
        handleCompleteToCreateDialog(data);
    }, [isSuccess, data, isPro]);

    useEffect(() => {
        if (!selectedWorkspacePlan) {
            setBody(prev => ({ ...prev, cloudId: '' }));
            return;
        }

        const cloud = clouds?.list.find(
            cloud => cloud.planType === (selectedWorkspacePlan === 'pro' ? 'pro' : 'basic')
        );

        setBody(prev => ({ ...prev, cloudId: cloud?.id }));
    }, [clouds, selectedWorkspacePlan]);

    useEffect(() => {
        if (isWorkspaceDialogOpen) {
            resetData();
        }
    }, [isWorkspaceDialogOpen]);

    useEffect(() => {
        if (!defaultRegion) {
            return;
        }
        setBody(prev => ({ ...prev, regionId: defaultRegion.id }));
    }, [defaultRegion]);

    useEffect(() => {
        resetSaveSecretBody();
        resetWorkspaceWithSecret();
    }, [accessKeyEnterMethod]);

    return (
        <>
            <Dialog open={isWorkspaceDialogOpen} onOpenChange={setWorkspaceDialog}>
                <DialogContent className="p-0" onInteractOutside={e => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle>{t('workspace.create.addWorkspace')}</DialogTitle>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <button className="absolute right-6 top-[19px] z-[51]" type="button">
                                    <X className="w-5 h-5" />
                                </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogTitle>
                                    {t('workspace.create.confirmStopWorkspaceCreation')}
                                </AlertDialogTitle>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>{t('project.create.goBack')}</AlertDialogCancel>
                                    <AlertDialogAction onClick={handleClose}>
                                        {t('project.create.stop')}
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </DialogHeader>

                    <form onSubmit={workspaceNameForm.handleSubmit(onSubmit)}>
                        <div className="p-[18px] overflow-auto">
                            <DialogDescription className="text-text text-[20px] font-semibold whitespace-pre-wrap">
                                {t('workspace.create.selectCloudAndEnterContent')}
                            </DialogDescription>

                            <div className="my-7 relative">
                                <Label variant="form">{t('workspace.create.workspaceName')}</Label>

                                <Controller
                                    name="name"
                                    rules={{
                                        required: t('workspace.create.nameRequiredErrorText'),
                                        validate: value => {
                                            const trimmed = value?.trim() || '';
                                            if (!textRegex.test(trimmed)) {
                                                return t('workspace.create.nameValidErrorText');
                                            }
                                            return true;
                                        },
                                    }}
                                    control={workspaceNameForm.control}
                                    render={({ field, fieldState }) => (
                                        <div className="w-full">
                                            <Input
                                                {...field}
                                                type="text"
                                                placeholder={t('workspace.create.enterName')}
                                            />
                                            {fieldState.error && (
                                                <p className="mt-1 ml-[6px] text-xs text-error font-medium">
                                                    {fieldState.error.message}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>

                            <Label variant="form" className="flex items-center justify-between">
                                {t('workspace.create.selectCloud')}
                                <button
                                    type="button"
                                    className="py-[2px] px-[6px] rounded bg-[#F5F5F5] dark:bg-[#454545] text-sm text-point flex items-center"
                                    onClick={openPriceDialog}
                                >
                                    {t('workspace.create.introducePricingPlan')}
                                    <ChevronRight className="w-[14px] h-[14px] text-point" />
                                </button>
                            </Label>
                            {CloudList}
                            {isPro && (
                                <div className="flex flex-col gap-[26px] mt-[26px]">
                                    <div>
                                        <Label variant="form">{t('workspace.create.selectRegionlabelText')}</Label>
                                        <Dialog open={isSelectRegionOpen} onOpenChange={setIsSelectRegionOpen}>
                                            {RegionButton}
                                            <DialogContent aria-describedby={undefined} className="pb-6 px-[18px]">
                                                <DialogTitle className="text-[18px] font-medium text-center">
                                                    {t('project.create.selectRegion')}
                                                </DialogTitle>
                                                <div className="flex flex-col gap-[13px] mt-4">
                                                    {regions?.list.map(region => (
                                                        <button
                                                            key={region.id}
                                                            onClick={() => setSelectedRegionId(region.id || '')}
                                                            className={`flex items-center gap-[6px] ${
                                                                region.id === selectedRegionId
                                                                    ? 'text-point'
                                                                    : 'text-sub'
                                                            }`}
                                                        >
                                                            {region.id === selectedRegionId ? (
                                                                <Check className="w-5 h-5" />
                                                            ) : (
                                                                <span className="w-5 h-5" />
                                                            )}
                                                            {region.name}
                                                        </button>
                                                    ))}
                                                </div>
                                                <Button
                                                    type="button"
                                                    size="lg"
                                                    className="w-full mt-7"
                                                    onClick={closeSelectRegionDialog}
                                                >
                                                    {t('workspace.create.complete')}
                                                </Button>
                                            </DialogContent>
                                        </Dialog>
                                    </div>
                                    <div>
                                        <Label variant="form">
                                            {t('workspace.create.accessKeyEnterMethodlabelText')}
                                        </Label>
                                        <div className="flex items-center gap-[14px]">
                                            <button
                                                type="button"
                                                onClick={() => accessKeyMethodBtnTapped('manual')}
                                                className={`w-full border ${
                                                    isManual ? 'border-point' : 'border-placeholder'
                                                } rounded-[10px] flex items-center gap-[10px] py-[10px] px-3`}
                                            >
                                                <div
                                                    className={`w-[26px] h-[26px] rounded-full flex items-center justify-center ${
                                                        isManual ? 'bg-point' : 'border border-placeholder'
                                                    }`}
                                                >
                                                    <Check
                                                        className={`${
                                                            isManual ? 'text-white' : 'text-placeholder'
                                                        } w-4 h-4`}
                                                        strokeWidth={3}
                                                    />
                                                </div>
                                                <div className={`${isManual ? '' : 'text-placeholder'} text-sm`}>
                                                    {t('workspace.create.manualEntryButtonText')}
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => accessKeyMethodBtnTapped('auto')}
                                                className={`w-full border ${
                                                    isAuto ? 'border-point' : 'border-placeholder'
                                                } rounded-[10px] flex items-center gap-[10px] py-[10px] px-3`}
                                            >
                                                <div
                                                    className={`w-[26px] h-[26px] rounded-full flex items-center justify-center ${
                                                        isAuto ? 'bg-point' : 'border border-placeholder'
                                                    }`}
                                                >
                                                    <Check
                                                        className={`${
                                                            isAuto ? 'text-white' : 'text-placeholder'
                                                        } w-4 h-4`}
                                                        strokeWidth={3}
                                                    />
                                                </div>
                                                <div className={`${isAuto ? '' : 'text-placeholder'} text-sm`}>
                                                    {t('workspace.create.autoGenerationButtonText')}
                                                </div>
                                            </button>
                                        </div>
                                    </div>

                                    {(isAuto || isManual) && (
                                        <div>
                                            <Label variant="form">
                                                {t(
                                                    isAuto
                                                        ? 'workspace.create.autoAccessKeyLabelText'
                                                        : 'workspace.create.manualAccessKeyLabelText'
                                                )}
                                            </Label>
                                            <button
                                                type="button"
                                                onClick={enterAccessKeyBtnTapped}
                                                className={`w-full h-[52px] px-[18px] rounded-[10px] bg-box border flex items-center justify-between ${
                                                    isAccessKeyEntered
                                                        ? 'border-text-input-focus-border'
                                                        : 'border-text-input-border'
                                                }`}
                                            >
                                                <div
                                                    className={`text-sm flex items-center gap-[6px] font-medium ${
                                                        isAccessKeyEntered ? '' : 'opacity-50'
                                                    }`}
                                                >
                                                    <Check className="w-4 h-4" />
                                                    {t(
                                                        isAuto
                                                            ? 'workspace.create.autoCreationKeyEnterButtonText'
                                                            : 'workspace.create.accessKeyEnterButtonText'
                                                    )}
                                                </div>
                                                <ChevronRight className="w-5 h-5" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="w-full text-sm font-medium flex items-center justify-between my-7">
                                <button
                                    type="button"
                                    className="flex items-center gap-[6px]"
                                    onClick={() => setAgree(prev => !prev)}
                                >
                                    <div className="w-4 h-4 border-[1.5px] border-primary rounded-full flex items-center justify-center">
                                        {agree && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                                    </div>
                                    <div className="text-sm text-sub cursor-pointer">
                                        {t('workspace.create.termsOfServiceAgreement')}({t('workspace.create.required')}
                                        )
                                    </div>
                                </button>
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <button
                                            className="text-sm text-gray-600 underline hover:text-gray-900"
                                            type="button"
                                        >
                                            {t('workspace.create.view')}
                                        </button>
                                    </DialogTrigger>
                                    <DialogPortal>
                                        <DialogContent
                                            ref={scrollDivRef}
                                            className="max-md:max-w-full max-md:w-full max-md:max-h-[100vh] max-w-[800px] max-md:rounded-none p-0"
                                        >
                                            <DialogHeader>
                                                <DialogTitle>Privacy & Terms</DialogTitle>
                                            </DialogHeader>
                                            <PrivacyTermsPage isDialog={true} />
                                        </DialogContent>
                                    </DialogPortal>
                                </Dialog>
                            </div>
                        </div>
                        <DialogFooter className="px-[18px]">
                            <Button size="lg" className="w-full" disabled={isCreatingWorkspace || isDisabled}>
                                {t(isCreatingWorkspace ? 'workspace.create.loading' : 'workspace.create.create')}
                            </Button>
                        </DialogFooter>
                    </form>

                    <Dialog open={isKeyFormOpen} onOpenChange={setIsKeyFormOpen}>
                        <DialogContent className="p-6 pt-[42px]" aria-describedby={undefined}>
                            <DialogTitle className="text-center mb-4 text-[18px]">
                                {t('workspace.create.accessKeyEnterButtonText')}
                            </DialogTitle>

                            <Label variant="form" className="flex items-center gap-1">
                                <div>
                                    <strong className="text-error">*</strong>
                                    {t('workspace.create.manualAccessKeyLabelText')}
                                </div>
                                <button onClick={() => setIsKeyInfoOpen(true)}>
                                    <Info className="w-[14px] h-[14px] text-placeholder" />
                                </button>
                            </Label>
                            <Input
                                value={saveSecretBody.accessKey}
                                onChange={e => setSaveSecretBody(prev => ({ ...prev, accessKey: e.target.value }))}
                                placeholder={t('workspace.create.enterKey')}
                                className={isEnrollAccessKeyError ? 'border-error' : ''}
                            />

                            <div className="my-10">
                                <Label variant="form">
                                    <strong className="text-error">*</strong>
                                    {t('workspace.create.secretAccessKeyLabelText')}
                                </Label>
                                <Input
                                    value={saveSecretBody.secretKey}
                                    onChange={e =>
                                        setSaveSecretBody(prev => ({
                                            ...prev,
                                            secretKey: e.target.value,
                                        }))
                                    }
                                    placeholder={t('workspace.create.enterSecretAccessKey')}
                                    className={isEnrollAccessKeyError ? 'border-error' : ''}
                                />
                            </div>
                            {isEnrollAccessKeyError && (
                                <div className="mb-10 text-center text-error">{enrollAccessKeyErrorMessage}</div>
                            )}
                            {workspaceWithSecret && (
                                <div className="relative mt-5 mb-10">
                                    <Label variant="form">Account ID</Label>
                                    <Input value={workspaceWithSecret?.$secret?.accountNo} />
                                    <div className="absolute -top-8 h-8 py-1 px-2 rounded-md text-white bg-[#081837] inline">
                                        <div
                                            className="absolute bottom-[-6px] left-1/2 transform -translate-x-1/2 w-0 h-0
                    border-l-[5px] border-r-[5px] border-t-[7px] border-t-[#081837] border-transparent"
                                        ></div>
                                        {t('workspace.create.checkId')}
                                    </div>
                                </div>
                            )}
                            <Button
                                size="lg"
                                className="w-full"
                                onClick={enrollAccessKeyBtnTapped}
                                disabled={isEnrollAccessBtnDisabled}
                            >
                                {isEnrolling ? (
                                    <SharedSpinner />
                                ) : (
                                    t(
                                        workspaceWithSecret
                                            ? 'workspace.create.complete'
                                            : 'workspace.create.authenticateKey'
                                    )
                                )}
                            </Button>
                        </DialogContent>
                    </Dialog>
                    <Dialog open={isAutoKeyInfoFormOpen} onOpenChange={setIsAutoKeyInfoFormOpen}>
                        <DialogContent className="p-6 pt-[42px]" aria-describedby={undefined}>
                            <DialogTitle className="text-center mb-4 text-[18px]">
                                {t('workspace.create.autoGenerationTitleText')}
                            </DialogTitle>
                            <DialogDescription className="text-text font-medium mb-3 ml-2">
                                {t('workspace.create.autoGenerationDescText')}
                            </DialogDescription>

                            <form onSubmit={workspaceRequestForm.handleSubmit(onWorkspaceRequestSubmit)}>
                                <div className="flex flex-col gap-y-6 mb-6">
                                    <div className="flex flex-col">
                                        <Label variant="form">{t('workspace.create.emailLabelText')}</Label>
                                        <Controller
                                            name="email"
                                            rules={{
                                                required: t('accessKey.emailRequiredErrorText'),
                                                pattern: {
                                                    value: emailRegex,
                                                    message: t('accessKey.emailValidErrorText'),
                                                },
                                            }}
                                            control={workspaceRequestForm.control}
                                            render={({ field, fieldState }) => (
                                                <div className="w-full">
                                                    <Input
                                                        {...field}
                                                        type="email"
                                                        placeholder={t('workspace.create.emailInputPlaceholderText')}
                                                    />
                                                    {fieldState.error && (
                                                        <p className="mt-1 ml-[6px] text-xs text-error font-medium">
                                                            {fieldState.error.message}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <Label variant="form">{t('workspace.create.phoneLabelText')}</Label>
                                        <Controller
                                            name="phone"
                                            rules={{
                                                required: t('accessKey.phoneRequiredErrorText'),
                                                pattern: {
                                                    value: phoneRegex,
                                                    message: t('accessKey.phoneValidErrorText'),
                                                },
                                            }}
                                            control={workspaceRequestForm.control}
                                            render={({ field, fieldState }) => (
                                                <div className="w-full">
                                                    <Input
                                                        {...field}
                                                        type="phone"
                                                        placeholder={t('workspace.create.phoneInputPlaceholderText')}
                                                    />
                                                    {fieldState.error && (
                                                        <p className="mt-1 ml-[6px] text-xs text-error font-medium">
                                                            {fieldState.error.message}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>

                                <Button size="lg" className="w-full" onClick={autoGenerateAccessKeyConfirmBtnTapped}>
                                    {t('confirm')}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                    {/* 엑세스키 사용법 */}
                    <Sheet open={isKeyInfoOpen} onOpenChange={setIsKeyInfoOpen}>
                        <SheetContent className="overflow-auto" aria-describedby={undefined}>
                            <SheetHeader className="justify-end">
                                <SheetClose>
                                    <X className="w-5 h-5" />
                                </SheetClose>
                            </SheetHeader>

                            <Image className="w-full mt-5" src={KeyInfoImage} darkSrc={KeyInfoDarkImage} />
                        </SheetContent>
                    </Sheet>
                </DialogContent>
            </Dialog>
            <Dialog open={proWorkspaceConfirmDialogOpened} onOpenChange={setProWorkspaceConfirmDialogOpened}>
                <DialogContent
                    showCloseButton={false}
                    className="pb-6 px-[18px]"
                    aria-describedby={undefined}
                    onInteractOutside={e => e.preventDefault()}
                >
                    <DialogTitle className="text-[22px] whitespace-pre-wrap">
                        Pro Plan {t('workspace.complete.title')}
                    </DialogTitle>
                    <div className="mt-[6px] text-sub mb-6 whitespace-pre-wrap">{t('workspace.complete.desc')}</div>
                    <div className="text-[18px] font-medium mb-3">{t('workspace.complete.paymentDetails')}</div>
                    <div className="flex flex-col gap-2">
                        <Card className="bg-box py-[14px] px-[18px] rounded-lg flex flex-col gap-[10px]">
                            <div className="flex items-center justify-between">
                                <div className="text-sub">{t('workspace.complete.paymentHistory')}</div>
                                <div className="font-semibold">Pro Plan {t('workspace.complete.creation')}</div>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="text-sub">{t('workspace.complete.date')}</div>
                                <div className="font-semibold">{moment(data?.createdAt).format('YYYY. MM. DD')}</div>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="text-sub">{t('workspace.complete.dueDate')}</div>
                                <div className="font-semibold">{estimatedBillingDate}</div>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="text-sub">{t('workspace.complete.paymentAmount')}</div>
                                <div className="font-semibold">$0</div>
                            </div>
                        </Card>
                    </div>
                    <Button size="lg" className="w-full mt-9" onClick={handleProWorkspaceCompleteBtnClick}>
                        {t('workspace.create.complete')}
                    </Button>
                </DialogContent>
            </Dialog>
        </>
    );
}
